import { useEffect, useMemo, useState } from "react";

import api from "../api/client";
import { usePublishScreenData } from "../utils/aiScreenContext";
import PSAUnitRates from "../components/PSAUnitRates";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";

type Customer = { id: number; name: string };
type Contract = {
  id: number;
  customer_id: number;
  name: string;
  contract_type: string;
  status: string;
  start_date: string;
  end_date: string | null;
  monthly_retainer_cents: number;
  hourly_rate_cents: number;
  included_hours: number;
  project_total_cents: number;
  sla_policy_id: number | null;
  auto_invoice: boolean;
  billing_day_of_month: number;
  auto_send: boolean;
  last_invoiced_period_end: string | null;
  notes: string | null;
};

const TYPES = [
  { value: "msp_block", label: "MSP block (retainer + hours)" },
  { value: "msp_unlimited", label: "MSP unlimited (flat retainer)" },
  { value: "tm", label: "Time & materials" },
  { value: "project", label: "Fixed-price project" },
  // The dominant MSP agreement shape, and the one this product could not
  // express until 2026-09-04. Its rates live in their own editor because a
  // real per-device deal prices servers, workstations, network gear and seats
  // differently — see components/PSAUnitRates.tsx.
  { value: "per_unit", label: "Per device / per seat" },
];

const STATUSES = ["draft", "active", "expired", "cancelled"];

const blankDraft = (customerId: number): Partial<Contract> => ({
  customer_id: customerId,
  name: "",
  contract_type: "msp_block",
  status: "draft",
  start_date: new Date().toISOString().slice(0, 10),
  end_date: null,
  monthly_retainer_cents: 0,
  hourly_rate_cents: 0,
  included_hours: 0,
  project_total_cents: 0,
  auto_invoice: false,
  billing_day_of_month: 1,
  auto_send: false,
  notes: "",
});

export default function PSAContracts() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerId, setCustomerId] = useState<number | "">("");
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [editing, setEditing] = useState<Partial<Contract> | null>(null);
  // Which contract's per-unit rates are open, if any.
  const [ratesFor, setRatesFor] = useState<Contract | null>(null);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/api/v1/customers")
      .then((r) => setCustomers(r.data || []))
      .catch(() => setCustomers([]));
  }, []);

  const load = async () => {
    setErr("");
    const params: Record<string, unknown> = {};
    if (customerId !== "") params.customer_id = customerId;
    try {
      const r = await api.get("/api/v1/psa/contracts", { params });
      setContracts(r.data || []);
    } catch (e: unknown) {
      const ex = e as { response?: { data?: { detail?: string } } };
      setErr(ex.response?.data?.detail || "Failed to load contracts");
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const save = async () => {
    if (!editing) return;
    setErr("");
    setSaving(true);
    try {
      if (editing.id) {
        await api.patch(`/api/v1/psa/contracts/${editing.id}`, editing);
      } else {
        await api.post("/api/v1/psa/contracts", editing);
      }
      setEditing(null);
      load();
    } catch (e: unknown) {
      const ex = e as { response?: { data?: { detail?: string } } };
      setErr(ex.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c: Contract) => {
    if (!window.confirm(`Delete contract "${c.name}"?`)) return;
    try {
      await api.delete(`/api/v1/psa/contracts/${c.id}`);
      load();
    } catch (e: unknown) {
      const ex = e as { response?: { data?: { detail?: string } } };
      setErr(ex.response?.data?.detail || "Delete failed");
    }
  };

  const fmt = (cents: number) => `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
  const cName = (id: number) => customers.find((c) => c.id === id)?.name || `customer ${id}`;
  const cSort = useSort("name");

  // Publish the visible rows so the global AI Assist can answer
  // "what's on my screen". Projected explicitly: this payload goes
  // into a model prompt.
  usePublishScreenData(
    "Contracts",
    useMemo(
      () =>
        contracts.map((c) => ({
          name: c.name,
          type: c.contract_type,
          status: c.status,
          start: c.start_date,
          end: c.end_date,
          monthly_retainer: c.monthly_retainer_cents / 100,
          hourly_rate: c.hourly_rate_cents / 100,
          included_hours: c.included_hours,
          auto_invoice: c.auto_invoice,
        })),
      [contracts],
    ),
    undefined,
  );

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">PSA — Contracts</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Per-customer managed-services agreements. Drives the billing engine —
            generated invoices roll up time entries against the active contract's
            type (block / unlimited / T&amp;M / project).
          </p>
        </div>
        <button
          onClick={() =>
            setEditing(
              blankDraft(typeof customerId === "number" ? customerId : customers[0]?.id || 0),
            )
          }
          className="px-4 py-2 bg-brand text-white rounded-lg text-sm font-medium hover:opacity-90"
        >
          New contract
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 flex items-end gap-3">
        <label className="text-sm">
          <span className="block text-gray-600 dark:text-gray-300 mb-1">Filter by customer</span>
          <select
            value={customerId}
            onChange={(e) =>
              setCustomerId(e.target.value === "" ? "" : Number(e.target.value))
            }
            className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
          >
            <option value="">All customers</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {err && (
        <div className="bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 px-4 py-2 rounded-lg text-sm">{err}</div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300">
            <tr>
              <SortableTh label="Customer" sortKey="customer_id" sort={cSort.sort} onSort={cSort.toggle} />
              <SortableTh label="Contract" sortKey="name" sort={cSort.sort} onSort={cSort.toggle} />
              <SortableTh label="Type" sortKey="contract_type" sort={cSort.sort} onSort={cSort.toggle} />
              <SortableTh label="Status" sortKey="status" sort={cSort.sort} onSort={cSort.toggle} />
              <SortableTh label="Period" sortKey="start_date" sort={cSort.sort} onSort={cSort.toggle} />
              <SortableTh label="Retainer" sortKey="retainer" sort={cSort.sort} onSort={cSort.toggle} align="right" />
              <SortableTh label="Rate / incl." sortKey="hourly_rate_cents" sort={cSort.sort} onSort={cSort.toggle} align="right" />
              <th className="text-left px-3 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {sortRows(contracts, cSort.sort, (c, key) => {
              switch (key) {
                case "customer_id":
                  return cName(c.customer_id).toLowerCase();
                case "contract_type":
                  return (c.contract_type || "").toLowerCase();
                case "status":
                  return (c.status || "").toLowerCase();
                case "start_date":
                  return c.start_date ? Date.parse(c.start_date) : 0;
                case "retainer":
                  return c.contract_type === "project"
                    ? c.project_total_cents ?? 0
                    : c.monthly_retainer_cents ?? 0;
                case "hourly_rate_cents":
                  return c.hourly_rate_cents ?? 0;
                default:
                  return (c.name || "").toLowerCase();
              }
            }).map((c) => (
              <tr key={c.id} className="text-gray-900 dark:text-gray-100">
                <td className="px-3 py-2 text-xs">{cName(c.customer_id)}</td>
                <td className="px-3 py-2 font-medium">{c.name}</td>
                <td className="px-3 py-2 text-xs">{c.contract_type}</td>
                <td className="px-3 py-2 text-xs">{c.status}</td>
                <td className="px-3 py-2 text-xs">
                  {c.start_date} → {c.end_date || "open"}
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs">
                  {c.contract_type === "project"
                    ? fmt(c.project_total_cents)
                    : fmt(c.monthly_retainer_cents)}
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs">
                  {fmt(c.hourly_rate_cents)}/h
                  {c.included_hours > 0 ? ` · ${c.included_hours}h incl` : ""}
                </td>
                <td className="px-3 py-2">
                  {c.contract_type === "per_unit" && (
                    <button
                      onClick={() => setRatesFor(c)}
                      className="text-xs text-brand dark:text-brand-light hover:underline mr-3"
                    >
                      Rates
                    </button>
                  )}
                  <button
                    onClick={() => setEditing(c)}
                    className="text-xs text-brand dark:text-brand-light hover:underline mr-3"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => remove(c)}
                    className="text-xs text-red-600 hover:underline"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {contracts.length === 0 && (
              <tr>
                <td className="px-3 py-6 text-center text-gray-500 dark:text-gray-400 italic text-xs" colSpan={8}>
                  No contracts yet — create one to start billing this customer.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-2xl w-full mx-4 shadow-xl">
            <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
              {editing.id ? "Edit contract" : "New contract"}
            </h2>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Field label="Customer">
                <select
                  value={editing.customer_id ?? ""}
                  onChange={(e) =>
                    setEditing({ ...editing, customer_id: Number(e.target.value) })
                  }
                  disabled={!!editing.id}
                  className="w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                >
                  <option value="">Pick…</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Name">
                <input
                  value={editing.name ?? ""}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                />
              </Field>
              <Field label="Contract type">
                <select
                  value={editing.contract_type ?? "msp_block"}
                  onChange={(e) => setEditing({ ...editing, contract_type: e.target.value })}
                  className="w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                >
                  {TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Status">
                <select
                  value={editing.status ?? "draft"}
                  onChange={(e) => setEditing({ ...editing, status: e.target.value })}
                  className="w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Start date">
                <input
                  type="date"
                  value={editing.start_date ?? ""}
                  onChange={(e) => setEditing({ ...editing, start_date: e.target.value })}
                  className="w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                />
              </Field>
              <Field label="End date (blank = open-ended)">
                <input
                  type="date"
                  value={editing.end_date ?? ""}
                  onChange={(e) => setEditing({ ...editing, end_date: e.target.value || null })}
                  className="w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                />
              </Field>
              <Field label="Monthly retainer ($)">
                <input
                  type="number"
                  value={(editing.monthly_retainer_cents ?? 0) / 100}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      monthly_retainer_cents: Math.round(Number(e.target.value) * 100),
                    })
                  }
                  className="w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                />
              </Field>
              <Field label="Hourly rate / overage ($)">
                <input
                  type="number"
                  value={(editing.hourly_rate_cents ?? 0) / 100}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      hourly_rate_cents: Math.round(Number(e.target.value) * 100),
                    })
                  }
                  className="w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                />
              </Field>
              <Field label="Included hours (block only)">
                <input
                  type="number"
                  value={editing.included_hours ?? 0}
                  onChange={(e) =>
                    setEditing({ ...editing, included_hours: Number(e.target.value) })
                  }
                  className="w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                />
              </Field>
              <Field label="Project total $ (project only)">
                <input
                  type="number"
                  value={(editing.project_total_cents ?? 0) / 100}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      project_total_cents: Math.round(Number(e.target.value) * 100),
                    })
                  }
                  className="w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                />
              </Field>
              <div className="col-span-2 border-t dark:border-gray-700 pt-3 mt-1">
                <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Recurring invoicing
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={editing.auto_invoice ?? false}
                      onChange={(e) => setEditing({ ...editing, auto_invoice: e.target.checked })}
                    />
                    Auto-generate monthly invoices
                  </label>
                  <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={editing.auto_send ?? false}
                      disabled={!editing.auto_invoice}
                      onChange={(e) => setEditing({ ...editing, auto_send: e.target.checked })}
                    />
                    Finalize &amp; email automatically (else leave as draft)
                  </label>
                  <Field label="Billing day of month">
                    <input
                      type="number"
                      min={1}
                      max={28}
                      value={editing.billing_day_of_month ?? 1}
                      onChange={(e) =>
                        setEditing({ ...editing, billing_day_of_month: Number(e.target.value) })
                      }
                      className="w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                    />
                  </Field>
                </div>
                <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                  The daily sweep bills the prior calendar month once this day arrives.
                  {editing.last_invoiced_period_end
                    ? ` Last billed through ${editing.last_invoiced_period_end}.`
                    : ""}
                </p>
              </div>
              <div className="col-span-2">
                <Field label="Notes (internal)">
                  <textarea
                    value={editing.notes ?? ""}
                    onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                    rows={3}
                    className="w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                  />
                </Field>
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setEditing(null)}
                className="px-4 py-2 text-sm border dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/40"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="px-4 py-2 text-sm bg-brand text-white rounded-lg disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {ratesFor && (
        <PSAUnitRates
          contractId={ratesFor.id}
          contractName={ratesFor.name}
          onClose={() => {
            setRatesFor(null);
            // Rates can change what the contract bills, and the list shows
            // money, so re-read rather than leave a stale row on screen.
            load();
          }}
        />
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs text-gray-600 dark:text-gray-400 mb-1">{label}</span>
      {children}
    </label>
  );
}
